import { MatDialog } from '@angular/material/dialog';
import { HttpClient, HttpParams } from '@angular/common/http';
import { firstValueFrom, Observable, of, Subscriber, timeout } from 'rxjs';
import { Injectable } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Overlay } from '@angular/cdk/overlay';
import {
  ConfigService,
  FileEntity,
  FileSelected,
  FileUpdateDto,
  FileUploadDialogOptions,
  FileUploadResult,
  HTTP_REQUESTS_TIMEOUT,
  Image,
  PDFReportInformation,
  UploadFileWithProgressOptions,
  XeroxMessages,
} from 'src/app/core';
import { PaginationResult } from 'src/app/shared';
import { FileUploadPopupComponent } from '../components';

@Injectable({
  providedIn: 'root',
})
export class FilesService {
  constructor(
    private http: HttpClient,
    private uploadDialogBox: MatDialog,
    private configService: ConfigService,
    private snackbar: MatSnackBar,
    private overlay: Overlay
  ) {}

  public uploadedFile: PDFReportInformation | undefined;
  public uploadedStatus = '';

  /**
   * Send request to api and get infomration using fileId.
   * @param {number} id shared document ID.
   */
  public getSharedDocumentInformation(
    id: number
  ): Observable<PDFReportInformation> {
    const url = this.configService
      .getUrl('files.getPdfReportInformation')
      .replace(':id', id.toString());

    return this.http
      .get<PDFReportInformation>(url, { withCredentials: true })
      .pipe(timeout(HTTP_REQUESTS_TIMEOUT));
  }

  async uploadFile(
    file: File,
    name: string,
    isDocSharing?: boolean
  ): Promise<FileUploadResult> {
    try {
      const uploadUrl = this.configService.getUrl('files.uploadOrder');
      const formData = new FormData();

      formData.append(name || 'undefined', file);

      const uploadResult = await firstValueFrom(
        this.http
          .post<{ file: FileEntity; numpages: number }>(uploadUrl, formData, {
            withCredentials: true,
          })
          .pipe(timeout(HTTP_REQUESTS_TIMEOUT))
      );

      return {
        file: uploadResult.file,
        report: {
          numpages: uploadResult.numpages,
        },
      };
    } catch (err: any) {
      console.error(err);
      if (err?.status?.toString() === '429') {
        this.openDefaultSnakebar(
          XeroxMessages.USER_INFORMING.BAD_OR_SAD.TIME_OUT
        );
        throw new Error(XeroxMessages.USER_INFORMING.BAD_OR_SAD.TIME_OUT);
      }

      throw new Error(err as string);
    }
  }

  public getSharedDocument(
    page: number,
    limit: number = 8,
    filter?: string
  ): Observable<PaginationResult<FileEntity>> {
    const url = this.configService.getUrl('documents.index');
    const params = this.getMergedQueryParam(url, { page, limit, filter });

    return this.http
      .get<PaginationResult<FileEntity>>(url, {
        withCredentials: true,
        params,
      })
      .pipe(timeout(HTTP_REQUESTS_TIMEOUT));
  }

  public getUserSharedFiles(): Observable<FileEntity[]> {
    const url = this.configService.getUrl('files.shared');

    return new Observable((subscriber) => {
      this.http
        .get(url, { withCredentials: true })
        .pipe(timeout(HTTP_REQUESTS_TIMEOUT))
        .subscribe(
          (data) => {
            subscriber.next(data as any);
          },
          (error) => {
            subscriber.error(error);
          }
        );
    });
  }

  public getOneSingleFileWithHash(hash: string): Observable<FileEntity> {
    const url = this.configService
      .getUrl('files.getByHash')
      .replace(':hash', hash);
    return this.http
      .get<FileEntity>(url, { withCredentials: true })
      .pipe(timeout(HTTP_REQUESTS_TIMEOUT));
  }

  /** Get one single file by id */
  public getOneSingleFile(id: number): Observable<FileEntity> {
    const url = this.configService.getUrl('files.getById');

    return new Observable((subscriber) => {
      this.http
        .get<FileEntity>(url, { withCredentials: true })
        .pipe(timeout(HTTP_REQUESTS_TIMEOUT))
        .subscribe(
          (incomeData) => {
            subscriber.next(incomeData);
          },
          (incomeError) => {
            subscriber.next(incomeError);
          }
        );
    });
  }

  public uploadFileWithProgress(
    options: UploadFileWithProgressOptions
  ): Observable<any> {
    const isDocSharing = options.isDocSharing;
    const formData = new FormData();

    const name = options.name;
    const file = options.file;

    let endpoint = 'uploadOrder'; // Default

    if (options.urlName) endpoint = options.urlName;
    if (isDocSharing) endpoint = 'uploadDoc';

    const url = this.configService
      .getUrl(`files.${endpoint}`, options.apiVersion)
      .replace(':name', options.name);

    formData.append('file', file);

    return this.http
      .post<{ file: FileEntity; numpages: number }>(url, formData, {
        observe: 'events',
        reportProgress: true,
        withCredentials: true,
      })
      .pipe(timeout(HTTP_REQUESTS_TIMEOUT));
  }

  public uploadImagesWithProgress(fileId: number, images: FileSelected[]) {
    const url = this.configService.getUrl('files.uploadImagesForFile');

    return new Observable<Image[]>((subscriber) => {
      const formData = new FormData();

      for (let image of images) {
        formData.append(image.name, image.file as unknown as File);
      }

      this.http
        .post<Image[]>(url, formData, { withCredentials: true })
        .pipe(timeout(HTTP_REQUESTS_TIMEOUT))
        .subscribe(
          (data) => subscriber.next(data),
          (error) => subscriber.error(error)
        );
    });
  }

  /**
   * Within this method we generate our first page preview
   */
  generateFirstPagePreview(
    fileId: string,
    caption?: string
  ): Observable<Image[]> {
    const previewUrl = this.configService.getUrl('files.firstPagePreview');

    return new Observable((subscriber) => {
      this.http
        .get<Image[]>(previewUrl)
        .pipe(timeout(HTTP_REQUESTS_TIMEOUT))
        .subscribe((generatedPreviews) => {
          subscriber.next(generatedPreviews);
        }, this.errorHandler(subscriber));
    });
  }

  /**
   * Update file to share.
   * We update file or simply we share the file for others to use.
   */
  public updateFileToShare(fileUploadObj: FileUpdateDto) {
    const url = this.configService.getUrl('files.updateFile');

    return new Observable((subscriber) => {
      this.http
        .put(url, fileUploadObj, { withCredentials: true })
        .pipe(timeout(HTTP_REQUESTS_TIMEOUT))
        .subscribe(
          (result) => {
            subscriber.next(result);
          },
          (error) => {
            subscriber.error(error);
          }
        );
    });
  }

  /**
   * We open new file upload dialog box
   * then we return an observable to track the
   * upload result.
   * @param options
   */
  public openFileUploadDialog<EventType extends File | PDFReportInformation>(
    options: FileUploadDialogOptions<EventType>
  ): Observable<EventType> {
    return new Observable((subscriber) => {
      options = {
        ...options,
        ...{ data: { immediate: options.immediate, ...options.data } },
        ...(options.height && options.width
          ? {}
          : {
              width: '500px',
              height: '500px',
            }),

        scrollStrategy: this.overlay.scrollStrategies.noop(),
      };

      this.uploadDialogBox
        .open(
          options.component ? options.component : FileUploadPopupComponent,
          options
        )
        .afterClosed()
        .subscribe((event: EventType) => {
          subscriber.next(event);
        });
    });
  }

  private getMergedQueryParam(
    url: string,
    objectOfParams: Record<string, any>
  ): HttpParams {
    const params = new HttpParams({ fromObject: objectOfParams });
    return params;
  }

  private openDefaultSnakebar(message: string, duration?: number) {
    this.snackbar.open(message, 'باشه !', { duration: duration || 1000 });
  }

  private errorHandler(subscriber: Subscriber<any>) {
    return (filesServiceError: any) => {
      subscriber.error(filesServiceError);
      throw new Error(filesServiceError);
    };
  }
}
